import apiClient from '@/lib/api/apiClient'
import { extractErrorMessage } from '@/lib/utils/errorHandling'
import type {
  Organization,
  OrganizationListParams,
  OrganizationListResponse,
  OrganizationSubmitPayload,
} from '../types/organization'

export async function getOrganizations(
  params: OrganizationListParams = {},
): Promise<OrganizationListResponse> {
  try {
    const response = await apiClient.get<OrganizationListResponse>('/api/organizations', {
      params,
    })
    return response.data
  } catch (error) {
    throw new Error(extractErrorMessage(error) || 'Failed to load organizations')
  }
}

export async function getOrganization(orgId: number): Promise<Organization> {
  try {
    const response = await apiClient.get<Organization>(`/api/organizations/${orgId}`)
    return response.data
  } catch (error) {
    throw new Error(extractErrorMessage(error) || 'Failed to load organization')
  }
}

import type { OrganizationLogoFilePick } from '../types'

/**
 * Create/update org. Always uses multipart FormData (Angular: `submitForm` passes `{ upload: true }`).
 */
export async function saveOrganization(
  payload: OrganizationSubmitPayload,
  orgId?: number,
  files?: OrganizationLogoFilePick,
): Promise<Organization> {
  const url = orgId ? `/api/organizations/${orgId}` : '/api/organizations'
  try {
    const fd = new FormData()
    fd.append('upload', 'true')
    for (const k of Object.keys(payload) as (keyof OrganizationSubmitPayload)[]) {
      const v = payload[k]
      if (v === undefined || v === null) continue
      fd.append(k, String(v))
    }
    if (files?.org_logo) fd.append('org_logo', files.org_logo)
    if (files?.org_swoosh_logo) fd.append('org_swoosh_logo', files.org_swoosh_logo)
    if (files?.org_image) fd.append('org_image', files.org_image)
    if (files?.ach_form_file) fd.append('ach_form_file', files.ach_form_file)
    if (files?.statement_instruction_file) {
      fd.append('statement_instruction_file', files.statement_instruction_file)
    }
    const response = await apiClient.post<Organization>(url, fd)
    return response.data
  } catch (error) {
    throw new Error(extractErrorMessage(error) || 'Failed to save organization')
  }
}

/**
 * Save/discard certificate template draft for org_id === 1 (Angular updateDraft pattern).
 */
export async function patchOrganizationCertificateDraft(
  orgId: number,
  draftPayload: { certificate_template_draft: string },
): Promise<void> {
  try {
    await apiClient.post(`/api/organizations/${orgId}`, draftPayload)
  } catch (error) {
    throw new Error(extractErrorMessage(error) || 'Failed to save certificate template draft')
  }
}

export type { OrganizationLogoFilePick } from '../types'
